import apiClient from '../../modules/api-client.js';
import { ViewOnceCardState } from './view-once-card-state.js';
import { playbackPoint, setSource } from './view-once-resume.js';
import { ViewOnceSession } from './view-once-session.js';

const t = key => window.trans('common', key);
const composer = {
  enabled: false,
  selection: null,
  reset() { this.enabled = false; this.refresh(); },
  refresh() {
    const button = document.getElementById('view_once_toggle');
    if (!button) return;
    const photos = window.photoUpload?.getPhotoQueue() || [];
    const video = Boolean(window.videoSend?.hasVideo());
    const selection = photos.map(photo => photo.id).join(',') || (video ? 'video' : null);
    if (selection !== this.selection) this.enabled = false;
    this.selection = selection;
    // A view-once message carries no reply, so an active reply turns the toggle off.
    const replying = Boolean(document.getElementById('reply_to_id')?.value);
    if (replying) this.enabled = false;
    if (photos.some(photo => photo.uploadMode === 'private')) this.enabled = true;
    const videoMode = video ? window.videoSend?.viewOnceMode?.() : undefined;
    if (videoMode !== undefined) this.enabled = videoMode;
    button.hidden = !selection;
    button.disabled = replying || videoMode !== undefined || this.busy || photos.length > 1 || photos.some(photo => photo.uploadMode) || Boolean(window.videoSend?.isSending());
    button.setAttribute('aria-pressed', String(this.enabled));
    const icon = button.querySelector('[data-view-once-icon]');
    icon.classList.toggle('bg-sky-500', this.enabled);
    icon.classList.toggle('text-white', this.enabled);
    icon.classList.toggle('border-transparent', this.enabled);
    icon.classList.toggle('border-dashed', !this.enabled);
    document.getElementById('message')?.classList.toggle('pr-12', Boolean(selection));
    this.syncPublicUploads(photos);
  },
  // A view-once photo must leave no public copy. Turning the toggle off uploads it publicly again.
  syncPublicUploads(photos) {
    const upload = window.photoUpload;
    if (!upload?.withdrawPublicUpload) return;
    for (const photo of photos.filter(entry => !entry.uploadMode)) {
      if (!this.enabled || photos.length > 1) upload.restorePublicUpload(photo.id);
      else if (!photo.publicWithdrawn) upload.withdrawPublicUpload(photo.id).catch(() => {});
    }
  },
};
window.viewOnceComposer = composer;
let active = null;
let toastTimer;

function closeViewer() {
  if (!active) return;
  const { session, root, media, focus, overflow, prior } = active;
  active = null;
  media.pause?.();
  media.removeAttribute('src');
  media.load?.();
  root.remove();
  document.body.style.overflow = overflow;
  session.close();
  // The card shows Opened at once. A claim the server never answered leaves the view unspent.
  void session.unclaimed().then(open => { if (open) cardState.reopen(focus, prior); });
  focus?.focus();
}

async function loadViewer(view) {
  if (view.busy) return;
  view.busy = true;
  view.retry.hidden = true;
  view.error.textContent = '';
  try {
    const result = await view.session.load();
    if (!result || active !== view) return;
    setSource(view.media, result.url, playbackPoint(view.media));
    view.caption.textContent = result.metadata.caption || '';
  } catch (error) {
    if (active !== view) return;
    const terminal = ['403', '404', '409', '410'].includes(error.message);
    view.error.textContent = t(terminal ? 'pm_view_once_unavailable' : 'pm_view_once_load_failed');
    view.retry.hidden = terminal;
  } finally { view.busy = false; }
}

function openViewer(button) {
  if (active || document.hidden || button.disabled) return;
  const random = crypto.getRandomValues(new Uint8Array(32));
  const token = Array.from(random, byte => byte.toString(16).padStart(2, '0')).join('');
  const session = new ViewOnceSession(button.dataset.viewOnceId, apiClient.request.bind(apiClient), token);
  const root = document.createElement('div');
  root.className = 'fixed inset-0 z-[10000] bg-gray-900 text-white';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', t('pm_view_once'));
  root.innerHTML = '<div class="flex h-full flex-col p-4 gap-3"><div class="flex justify-between"><button data-close class="h-11 px-4"></button><button data-report class="h-11 px-4"></button></div><div data-host class="flex flex-1 min-h-0 items-center justify-center"></div><p data-caption class="text-center whitespace-pre-wrap break-words"></p><p data-error role="status" class="text-center"></p><button data-retry hidden class="button-primary min-h-11 self-center px-5 rounded-full"></button></div>';
  const media = document.createElement(button.dataset.viewOnceKind === 'video' ? 'video' : 'img');
  media.className = 'max-h-full max-w-full object-contain';
  if (media.tagName === 'VIDEO') {
    media.controls = true;
    media.playsInline = true;
    media.disablePictureInPicture = true;
    media.setAttribute('controlsList', 'nodownload noremoteplayback');
  } else media.alt = '';
  media.referrerPolicy = 'no-referrer';
  media.draggable = false;
  root.oncontextmenu = event => event.preventDefault();
  root.querySelector('[data-host]').append(media);
  const close = root.querySelector('[data-close]');
  close.textContent = t('close');
  close.onclick = closeViewer;
  const report = root.querySelector('[data-report]');
  report.textContent = t('report');
  const messageId = button.closest('.pm_msg')?.id.replace(/^msg_/, '');
  report.onclick = () => { closeViewer(); if (messageId) window.report_message(messageId); };
  const retry = root.querySelector('[data-retry]');
  retry.textContent = t('try_again');
  const view = { session, root, media, retry, caption: root.querySelector('[data-caption]'),
    error: root.querySelector('[data-error]'), focus: button, overflow: document.body.style.overflow, busy: false,
    prior: { state: button.dataset.viewOnceState } };
  retry.onclick = () => loadViewer(view);
  media.onerror = () => {
    if (active !== view) return;
    view.error.textContent = t('pm_view_once_load_failed');
    view.retry.hidden = false;
  };
  button.disabled = true;
  button.dataset.viewOnceState = 'opened';
  document.body.append(root);
  document.body.style.overflow = 'hidden';
  active = view;
  close.focus();
  void loadViewer(view);
}

document.addEventListener('click', event => {
  const button = event.target.closest?.('#view_once_toggle');
  if (button && !button.disabled) {
    composer.enabled = !composer.enabled;
    composer.refresh();
    if (composer.enabled) window.clearPrivateCaptionDraft?.();
    const confirmation = document.getElementById('view_once_confirmation');
    confirmation.textContent = composer.enabled ? t(window.videoSend?.hasVideo() ? 'pm_view_once_video_set' : 'pm_view_once_photo_set') : '';
    confirmation.className = 'absolute bottom-full right-0 mb-2 rounded-lg bg-gray-800 text-white px-3 py-2 text-sm';
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { confirmation.textContent = ''; confirmation.className = 'sr-only'; }, 2500);
  }
  const card = event.target.closest?.('[data-view-once-id]');
  if (card) openViewer(card);
});
document.addEventListener('visibilitychange', () => { if (document.hidden) closeViewer(); });
window.addEventListener('pagehide', closeViewer);
document.addEventListener('keydown', event => {
  if (!active) return;
  if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); closeViewer(); }
  if (event.key === 'Tab') {
    const buttons = [...active.root.querySelectorAll('button')].filter(button => !button.hidden);
    const first = buttons[0], last = buttons[buttons.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }
}, true);

const cardState = new ViewOnceCardState();
document.addEventListener('thread:view-once-opened', event => {
  if (cardState.receive(event.detail)) cardState.apply(document);
});
// A pending thread response can contain a card from before the socket event.
const cardObserver = new MutationObserver(records => {
  if (cardState.opened.size === 0) return;
  if (records.some(record => record.addedNodes.length)) cardState.apply(document);
});
cardObserver.observe(document.body, { childList: true, subtree: true });
